import type { TransportMode } from '@tappa/shared';

/**
 * Apertura del navigatore.
 *
 * Si usa il link universale di Google Maps: su telefono apre direttamente
 * l'app (Android e iOS) con il percorso già impostato, su desktop apre il sito.
 * Non serve alcuna chiave API e non c'è alcun costo: è un semplice link.
 *
 * Il resto dell'app resta indipendente da Google — mappe, luoghi e percorsi
 * continuano ad arrivare da OpenStreetMap. Qui si tratta solo di consegnare la
 * destinazione al navigatore che la persona ha già sul telefono.
 */

const GOOGLE_TRAVEL_MODE: Record<TransportMode, string> = {
  walk: 'walking',
  bike: 'bicycling',
  car: 'driving',
  transit: 'transit',
  taxi: 'driving',
};

export interface NavigationTarget {
  readonly lat: number;
  readonly lon: number;
  readonly label?: string | undefined;
}

export interface NavigationOptions {
  /** Punto di partenza. Se assente, Google usa la posizione attuale. */
  readonly from?: { lat: number; lon: number } | null | undefined;
  readonly mode?: TransportMode | undefined;
}

export function googleMapsDirectionsUrl(
  target: NavigationTarget,
  options: NavigationOptions = {},
): string {
  const params = new URLSearchParams({
    api: '1',
    destination: `${String(target.lat)},${String(target.lon)}`,
    travelmode: GOOGLE_TRAVEL_MODE[options.mode ?? 'walk'],
  });

  if (options.from != null) {
    params.set('origin', `${String(options.from.lat)},${String(options.from.lon)}`);
  }

  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

/** Scheda del luogo su Google Maps, senza avviare il percorso. */
export function googleMapsPlaceUrl(target: NavigationTarget): string {
  const params = new URLSearchParams({
    api: '1',
    query: `${String(target.lat)},${String(target.lon)}`,
  });
  return `https://www.google.com/maps/search/?${params.toString()}`;
}

/** Vero quando l'app gira installata, senza la barra del browser. */
function appInstallata(): boolean {
  try {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      // Safari su iOS non implementa `display-mode`: usa una proprietà propria.
      (navigator as Navigator & { standalone?: boolean }).standalone === true
    );
  } catch {
    return false;
  }
}

/**
 * Apre il navigatore.
 *
 * Il modo di aprirlo cambia a seconda di dove siamo, e non è un dettaglio:
 *
 *   * **app installata** → si naviga nella scheda corrente. Da dentro una PWA,
 *     `window.open` apre una scheda del browser, e lì l'indirizzo di Google Maps
 *     resta il *sito web* invece di passare la mano all'app di navigazione: si
 *     finisce su una mappa che chiede di accedere, esattamente quando si ha
 *     bisogno delle indicazioni. Navigando direttamente, il sistema operativo
 *     riconosce l'indirizzo e apre l'app giusta;
 *   * **browser** → nuova scheda, così non si perde l'itinerario aperto.
 *     `noopener` è obbligatorio: senza, la pagina aperta potrebbe manipolare la
 *     nostra tramite `window.opener`.
 *
 * Se l'apertura in nuova scheda viene bloccata (capita con i blocchi popup), si
 * ripiega sulla navigazione diretta invece di non fare nulla.
 */
export function openNavigation(
  target: NavigationTarget,
  options: NavigationOptions = {},
): void {
  const url = googleMapsDirectionsUrl(target, options);

  if (appInstallata()) {
    window.location.href = url;
    return;
  }

  const aperta = window.open(url, '_blank', 'noopener,noreferrer');
  if (aperta === null) window.location.href = url;
}
